import { Injectable } from '@angular/core';
import { AbstractControl, FormArray, ValidationErrors } from '@angular/forms';
import { FormGroupQuestion } from '../components/re-orderable-theme/question-node/question-node.component';
import { FormGroupTheme } from '../components/re-orderable-theme/re-orderable-theme.component';
import { IDiscussionGuideQuestion } from '../models/data-capture.models';
import { QuestionHierarchyService } from './question-hierarchy.service';

@Injectable()
export class ThemeValidationService {
  constructor(private questionHierarchyService: QuestionHierarchyService) {}

  validate() {
    return (control: FormArray<FormGroupTheme>): ValidationErrors => {
      const themeNames = control.value.map((theme) =>
        this.normaliseName(theme.theme)
      );

      control.controls.forEach((themeGroup) => {
        this.errorIndividualControls(themeGroup, themeNames);
      });

      if (themeNames.some((name) => !name)) {
        return { noThemeName: true };
      }

      if (control.value.some((theme) => !theme.questions.length)) {
        return { noQuestions: true };
      }

      const questions = this.questionHierarchyService.flattenThemes(control);
      const normalisedQuestions = questions.map((question) => ({
        ...question,
        normalisedName: this.normaliseName(question.question),
      }));

      if (normalisedQuestions.some((question) => !question.normalisedName)) {
        return { noQuestionName: true };
      }

      const nonEmptyThemeNames = themeNames.filter(Boolean);

      if (nonEmptyThemeNames.length !== new Set(nonEmptyThemeNames).size) {
        return { duplicateThemeNames: true };
      }

      if (
        this.groupQuestionsByTheme(normalisedQuestions).some(({ questions }) =>
          [...new Set(questions.map((q) => q.parent_question_id))].some(
            (parentQuestionId) => {
              const names = questions
                .filter((q) => q.parent_question_id === parentQuestionId)
                .map((q) => q.normalisedName)
                .filter(Boolean);

              const uniqueNames = [...new Set(names)];
              return names.length !== uniqueNames.length;
            }
          )
        )
      ) {
        return { duplicateQuestionNames: true };
      }

      return null;
    };
  }

  private errorIndividualControls(
    themeGroup: FormGroupTheme,
    themeNames: string[]
  ) {
    const themeControl = themeGroup.controls.theme;
    const questionsArray = themeGroup.controls.questions;

    const normalisedThemeName = this.normaliseName(themeControl.value);

    this.setControlError(themeControl, 'noThemeName', !normalisedThemeName);

    const duplicateThemes = themeNames.filter(
      (t, i, arr) => arr.indexOf(t) !== i
    );

    this.setControlError(
      themeControl,
      'duplicateThemeNames',
      normalisedThemeName && duplicateThemes.includes(normalisedThemeName)
    );

    this.setControlError(themeGroup, 'noQuestions', !questionsArray.length);

    const allQuestions = this.collectQuestions(questionsArray);
    const normalisedQuestionNames = new Map<string, string>();
    const questionsByParent = new Map<string | null, FormGroupQuestion[]>();

    allQuestions.forEach((questionGroup) => {
      const questionControl = questionGroup.controls.question;
      const normalisedQuestionName = this.normaliseName(questionControl.value);
      const questionId = questionGroup.controls.question_id.value;

      normalisedQuestionNames.set(questionId, normalisedQuestionName);

      this.setControlError(
        questionControl,
        'noQuestionName',
        !normalisedQuestionName
      );

      const parentId = questionGroup.controls.parent_question_id.value || null;
      const bucket = questionsByParent.get(parentId) || [];

      bucket.push(questionGroup);
      questionsByParent.set(parentId, bucket);
    });

    questionsByParent.forEach((questions) => {
      const names = questions
        .map((q) => normalisedQuestionNames.get(q.controls.question_id.value))
        .filter(Boolean);

      const duplicateNames = names.filter(
        (name, i, arr) => arr.indexOf(name) !== i
      );

      questions.forEach((questionGroup) => {
        const questionControl = questionGroup.controls.question;
        const normalisedName = normalisedQuestionNames.get(
          questionGroup.controls.question_id.value
        );

        this.setControlError(
          questionControl,
          'duplicateQuestionNames',
          normalisedName && duplicateNames.includes(normalisedName)
        );
      });
    });
  }

  private groupQuestionsByTheme(
    discussion_guide: (IDiscussionGuideQuestion & {
      normalisedName: string;
    })[]
  ) {
    const themes = discussion_guide.map((question) => question.theme);

    return [...new Set(themes)].map((theme) => ({
      theme,
      questions: discussion_guide.filter(
        (question) => question.theme === theme
      ),
    }));
  }

  private collectQuestions(
    questions: FormArray<FormGroupQuestion>
  ): FormGroupQuestion[] {
    return questions.controls.flatMap((question) => [
      question,
      ...this.collectQuestions(question.controls.children),
    ]);
  }

  private setControlError(
    control: AbstractControl,
    errorKey: string,
    shouldSet: boolean
  ) {
    const errors = control.errors || {};

    if (shouldSet) {
      if (!errors[errorKey]) {
        control.setErrors({ ...errors, [errorKey]: true });
      }
      return;
    }

    if (!errors[errorKey]) {
      return;
    }

    const { [errorKey]: _, ...rest } = errors;
    control.setErrors(Object.keys(rest).length ? rest : null);
  }

  private normaliseName(value: string | null | undefined) {
    return (value || '').trim().toLowerCase();
  }
}
